import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { hasAnyPermission, hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import '../../styles/admin.css';
import './AdminItemDetallePage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  if (sanitized.startsWith('items/images/')) return `${base}/uploads/${sanitized}`;
  return `${base}/uploads/items/images/${sanitized}`;
};

const AdminItemDetalleInner = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();

  // Verificar permisos
  const hasEditItems = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_ITEMS), [user]);
  const hasActivateItems = useMemo(() => hasPermission(user, PERMISSION_IDS.ACTIVAR_ITEMS), [user]);
  const hasArchiveItems = useMemo(() => hasPermission(user, PERMISSION_IDS.ARCHIVAR_ITEMS), [user]);
  const canViewItemDetail = useMemo(
    () =>
      hasAnyPermission(user, [
        PERMISSION_IDS.VER_ITEMS,
        PERMISSION_IDS.VER_TODOS_ITEMS,
        PERMISSION_IDS.PANEL_ITEMS,
        PERMISSION_IDS.EDITAR_ITEMS,
      ]),
    [user],
  );

  useEffect(() => {
    if (user && !canViewItemDetail) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para ver este item.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, canViewItemDetail, navigate, notify]);

  const [item, setItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [pending, setPending] = useState(false);

  // Estados de edición
  const [editState, setEditState] = useState({
    name: { value: '', editing: false },
    shortDescription: { value: '', editing: false },
    fullDescription: { value: '', editing: false },
    sku: { value: '', editing: false },
    stock: { value: 0, editing: false },
    offer: { value: null, editing: false },
    category: { value: null, editing: false },
  });

  // Estados de imágenes
  const [images, setImages] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [expandedImages, setExpandedImages] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Estados de atributos
  const [attributes, setAttributes] = useState([]);
  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [attributeDraft, setAttributeDraft] = useState({ id: null, key: '', value: '' });
  const [attributeLoading, setAttributeLoading] = useState(false);
  const [expandedAttributes, setExpandedAttributes] = useState(true);
  const [variantes, setVariantes] = useState([]);
  const [variantLoading, setVariantLoading] = useState(false);
  const [variantImageUploadIndex, setVariantImageUploadIndex] = useState(null);
  const [expandedVariantes, setExpandedVariantes] = useState(true);

  const fetchItem = useCallback(async () => {
    if (!token || !itemId) return;
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl(`/api/items/${itemId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`No se pudo cargar el item (${response.status})`);
      }

      const data = await response.json();
      const itemData = data.item || data;
      setItem(itemData);
      
      // Normalizar imágenes
      const normalizedImages = (itemData.images || []).map(img => ({
        id: img.id,
        image_url: img.image_url,
        position: img.position || 0,
        alt_text: img.alt_text || '',
      })).sort((a, b) => a.position - b.position);
      setImages(normalizedImages);
      setCurrentImageIndex(0);
      
      // Normalizar atributos
      const normalizedAttributes = (itemData.attributes || []).map(attr => ({
        id: attr.id,
        attribute_key: attr.attribute_key || attr.key || '',
        attribute_value: attr.attribute_value || attr.value || '',
      }));
      setAttributes(normalizedAttributes);

      setVariantes(
        (itemData.variantes || []).map((v) => ({
          id: v.id,
          nombre_variante: v.nombre_variante || '',
          precio_variante:
            v.precio_variante != null && v.precio_variante !== ''
              ? Number(v.precio_variante)
              : 0,
          imagen_variante: v.imagen_variante || '',
          stock_variante: Number.isFinite(Number(v.stock_variante))
            ? Math.max(0, parseInt(String(v.stock_variante), 10) || 0)
            : 0,
          oferta_variante:
            v.oferta_variante != null && v.oferta_variante !== ''
              ? Number(v.oferta_variante ?? v.OfertaDeVariable)
              : Number(v.OfertaDeVariable) || 0,
        })),
      );
      
      setEditState({
        name: { value: itemData.NombreItem || '', editing: false },
        shortDescription: { value: itemData.descripcionbreveitem || '', editing: false },
        fullDescription: { value: itemData.descripcioncompletaitem || '', editing: false },
        sku: { value: itemData.sku || '', editing: false },
        stock: { value: itemData.stockdisponible || 0, editing: false },
        offer: { value: itemData.Oferta || null, editing: false },
        category: { value: itemData.IDCategoria || null, editing: false },
      });
    } catch (error) {
      notify({
        title: 'Error al cargar el item',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [itemId, notify, token]);

  const fetchCategories = useCallback(async () => {
    if (!token) return;
    setLoadingCategories(true);
    try {
      const response = await fetch(buildApiUrl('/api/categorias'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('No se pudieron cargar las categorías');
      }

      const data = await response.json();
      setCategories(Array.isArray(data.categorias) ? data.categorias : []);
    } catch (error) {
      logError('Error cargando categorías:', error);
    } finally {
      setLoadingCategories(false);
    }
  }, [token]);

  useEffect(() => {
    fetchItem();
    fetchCategories();
  }, [fetchItem, fetchCategories]);

  const updateItem = useCallback(async (updates) => {
    if (!token || !itemId) return;
    setPending(true);
    try {
      const response = await fetch(buildApiUrl(`/api/items/${itemId}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar el item');
      }

      const data = await response.json();
      await fetchItem();
      return data;
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
      throw error;
    } finally {
      setPending(false);
    }
  }, [itemId, token, notify, fetchItem]);

  const uploadItemImageFile = useCallback(
    async (file) => {
      if (!file) {
        throw new Error('Seleccioná una imagen');
      }
      const formData = new FormData();
      formData.append('itemImage', file);

      const uploadResponse = await fetch(buildApiUrl('/api/items/upload-image'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({ error: 'Error al subir la imagen' }));
        throw new Error(errorData.error || `Error al subir la imagen (${uploadResponse.status})`);
      }

      const uploadData = await uploadResponse.json();
      const imagePath = uploadData.url || uploadData.file?.publicUrl;
      if (!imagePath) {
        throw new Error('No se recibió la ruta de la imagen');
      }
      return imagePath;
    },
    [token],
  );

  const handleVariantImageUpload = useCallback(
    async (index, file) => {
      if (!file) return;
      setVariantImageUploadIndex(index);
      try {
        const imagePath = await uploadItemImageFile(file);
        setVariantes((prev) =>
          prev.map((r, i) => (i === index ? { ...r, imagen_variante: imagePath } : r)),
        );
        notify({
          title: 'Imagen subida',
          message: 'La imagen quedó asignada a la variante. Recordá guardar variantes.',
          variant: 'success',
        });
      } catch (error) {
        notify({ title: 'Error', message: error.message, variant: 'error' });
      } finally {
        setVariantImageUploadIndex(null);
      }
    },
    [uploadItemImageFile, notify],
  );

  const handleClearVariantImage = useCallback((index) => {
    setVariantes((prev) =>
      prev.map((r, i) => (i === index ? { ...r, imagen_variante: '' } : r)),
    );
  }, []);

  const handleSaveField = useCallback(async (field, value) => {
    const updateData = {};
    
    switch (field) {
      case 'name':
        updateData.NombreItem = value;
        break;
      case 'shortDescription':
        updateData.descripcionbreveitem = value;
        break;
      case 'fullDescription':
        updateData.descripcioncompletaitem = value;
        break;
      case 'sku':
        updateData.sku = value;
        break;
      case 'stock':
        updateData.stockdisponible = parseInt(value) || 0;
        break;
      case 'offer':
        updateData.Oferta = value ? parseFloat(value) : null;
        break;
      case 'category':
        updateData.IDCategoria = parseInt(value);
        break;
      default:
        return;
    }

    try {
      await updateItem(updateData);
      setEditState(prev => ({
        ...prev,
        [field]: { ...prev[field], editing: false },
      }));
      notify({
        title: 'Éxito',
        message: 'Campo actualizado correctamente',
        variant: 'success',
      });
    } catch (error) {
      // Error ya manejado en updateItem
    }
  }, [updateItem, notify]);

  const handleToggleActive = useCallback(async () => {
    if (!window.confirm(`¿Estás seguro de que quieres ${item.is_active ? 'desactivar' : 'activar'} este item?`)) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/items/${itemId}/activate`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: !item.is_active }),
      });

      if (!response.ok) {
        throw new Error('Error al cambiar el estado del item');
      }

      const data = await response.json();
      setItem((prev) => ({ ...prev, is_active: data.result?.is_active ?? !prev.is_active }));
      notify({
        title: 'Éxito',
        message: `Item ${data.result?.is_active ? 'activado' : 'desactivado'} correctamente`,
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [item?.is_active, itemId, token, notify]);

  const handleToggleArchive = useCallback(async () => {
    if (!window.confirm(`¿Estás seguro de que quieres ${item.archivado ? 'desarchivar' : 'archivar'} este item?`)) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/items/${itemId}/archive`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al cambiar el estado de archivo');
      }

      const data = await response.json();
      setItem((prev) => ({ ...prev, archivado: data.result?.archivado ?? !prev.archivado }));
      notify({
        title: 'Éxito',
        message: `Item ${data.result?.archivado ? 'archivado' : 'desarchivado'} correctamente`,
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [item?.archivado, itemId, token, notify]);

  const handleUploadImage = useCallback(async () => {
    if (!imageFile) {
      notify({ title: 'Error', message: 'Selecciona una imagen', variant: 'error' });
      return;
    }

    setImageLoading(true);
    try {
      const imageUrl = await uploadItemImageFile(imageFile);

      // Agregar la nueva imagen a la lista
      const newImages = [...images, {
        id: null,
        image_url: imageUrl,
        position: images.length,
        alt_text: '',
      }];

      await updateItem({ images: newImages.map((img, idx) => ({
        image_url: img.image_url,
        position: idx,
        alt_text: img.alt_text,
      })) });

      // Mover el carrusel a la última imagen agregada
      if (newImages.length > 2) {
        setCurrentImageIndex(Math.floor((newImages.length - 1) / 2) * 2);
      }

      setShowImageModal(false);
      setImageFile(null);
      notify({
        title: 'Éxito',
        message: 'Imagen subida correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    } finally {
      setImageLoading(false);
    }
  }, [imageFile, images, notify, updateItem, uploadItemImageFile]);

  const handleDeleteImage = useCallback(async (imageId, position) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta imagen?')) {
      return;
    }

    try {
      const newImages = images.filter(img => img.id !== imageId);
      await updateItem({ images: newImages.map((img, idx) => ({
        image_url: img.image_url,
        position: idx,
        alt_text: img.alt_text,
      })) });
      
      // Ajustar el índice del carrusel si es necesario
      if (currentImageIndex >= newImages.length - 1 && newImages.length > 0) {
        setCurrentImageIndex(Math.max(0, Math.ceil(newImages.length / 2) - 1) * 2);
      }
      
      notify({
        title: 'Éxito',
        message: 'Imagen eliminada correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [images, updateItem, notify, currentImageIndex]);

  const handleSaveAttributes = useCallback(async () => {
    setAttributeLoading(true);
    try {
      // Filter out attributes with empty key or value before sending
      const validAttributes = attributes.filter(attr => attr.attribute_key?.trim() && attr.attribute_value?.trim());
      await updateItem({ attributes: validAttributes });
      setShowAttributeModal(false);
      notify({
        title: 'Éxito',
        message: 'Atributos guardados correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    } finally {
      setAttributeLoading(false);
    }
  }, [attributes, updateItem, notify]);

  const handleAddAttribute = useCallback(() => {
    if (!attributeDraft.key.trim() || !attributeDraft.value.trim()) {
      notify({ title: 'Error', message: 'La clave y el valor son requeridos', variant: 'error' });
      return;
    }

    const newAttribute = {
      id: null,
      attribute_key: attributeDraft.key.trim(),
      attribute_value: attributeDraft.value.trim(),
    };

    setAttributes(prev => [...prev, newAttribute]);
    setAttributeDraft({ id: null, key: '', value: '' });
    setShowAttributeModal(false);
  }, [attributeDraft, notify]);

  const handleEditAttribute = useCallback((index) => {
    const attr = attributes[index];
    setAttributeDraft({ id: attr.id, key: attr.attribute_key, value: attr.attribute_value });
    setShowAttributeModal(true);
  }, [attributes]);

  const handleUpdateAttribute = useCallback(() => {
    if (!attributeDraft.key.trim() || !attributeDraft.value.trim()) {
      notify({ title: 'Error', message: 'La clave y el valor son requeridos', variant: 'error' });
      return;
    }

    setAttributes(prev => prev.map((attr, idx) => {
      if (attr.id === attributeDraft.id || (!attr.id && idx === attributes.findIndex(a => !a.id))) {
        return {
          ...attr,
          attribute_key: attributeDraft.key.trim(),
          attribute_value: attributeDraft.value.trim(),
        };
      }
      return attr;
    }));

    setAttributeDraft({ id: null, key: '', value: '' });
    setShowAttributeModal(false);
  }, [attributeDraft, attributes, notify]);

  const handleDeleteAttribute = useCallback((index) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este atributo?')) {
      return;
    }

    setAttributes(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleSaveVariantes = useCallback(async () => {
    setVariantLoading(true);
    try {
      const payload = variantes
        .filter((row) => row.nombre_variante?.trim())
        .map((row) => {
          const rawOferta = parseFloat(row.oferta_variante);
          const ofertaVar = Number.isNaN(rawOferta)
            ? 0
            : Math.min(100, Math.max(0, rawOferta));
          return {
            nombre_variante: row.nombre_variante.trim(),
            precio_variante: Number(row.precio_variante) || 0,
            imagen_variante: row.imagen_variante?.trim() || null,
            stock_variante: Math.max(0, parseInt(String(row.stock_variante), 10) || 0),
            OfertaDeVariable: ofertaVar,
          };
        });
      await updateItem({ variantes: payload });
      notify({
        title: 'Éxito',
        message: 'Variantes guardadas correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    } finally {
      setVariantLoading(false);
    }
  }, [variantes, updateItem, notify]);

  const handleAddVariantRow = useCallback(() => {
    setVariantes((prev) => [
      ...prev,
      {
        id: null,
        nombre_variante: '',
        precio_variante: 0,
        imagen_variante: '',
        stock_variante: 0,
        oferta_variante: 0,
      },
    ]);
  }, []);

  const handleRemoveVariantRow = useCallback((index) => {
    setVariantes((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="items" />
          <main className="admin-main">
            <div className="AIDP-container">
              <div className="AIDP-loading">
                <Spinner animation="border" />
                <p>Cargando item...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="items" />
          <main className="admin-main">
            <div className="AIDP-container">
              <div className="AIDP-error">
                <h2>Item no encontrado</h2>
                <p>El item que buscas no existe o fue eliminado.</p>
                <Button variant="primary" onClick={() => navigate('/admin/items')}>
                  Volver a Items
                </Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const categoryName = categories.find(c => c.id === item.IDCategoria)?.NombreCategoria || 'Sin categoría';
  const mainImage = images.length > 0 ? images[0] : null;

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar activeKey="items" onNavigate={(item) => navigate(item.path)} />
        <main className="admin-main">
          <div className="AIDP-container">
            {/* Header */}
            <div className="AIDP-header">
              <div className="AIDP-header-content">
                <button
                  type="button"
                  className="AIDP-back-button"
                  onClick={() => navigate('/admin/items')}
                >
                  <i className="bi-arrow-left-circle" />
                  Volver
                </button>
                <h1 className="AIDP-title">
                  <i className="bi-box-seam-fill" />
                  {item.NombreItem || 'Item sin nombre'}
                </h1>
                <p className="AIDP-subtitle">
                  SKU: {item.sku || 'N/A'} • Categoría: {categoryName}
                </p>
              </div>
              <div className="AIDP-actions">
                <span className={`AIDP-badge AIDP-badge-${item.is_active ? 'success' : 'secondary'}`}>
                  {item.is_active ? 'Activo' : 'Inactivo'}
                </span>
                {item.archivado && <span className="AIDP-badge AIDP-badge-dark">Archivado</span>}
                {hasActivateItems && (
                  <button
                    type="button"
                    className={`AIDP-action-btn AIDP-action-btn-${item.is_active ? 'warning' : 'success'}`}
                    onClick={handleToggleActive}
                    disabled={pending}
                  >
                    <i className={`bi-toggle-${item.is_active ? 'on' : 'off'}`} />
                    {item.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                )}
                {hasArchiveItems && (
                  <button
                    type="button"
                    className={`AIDP-action-btn AIDP-action-btn-outline`}
                    onClick={handleToggleArchive}
                    disabled={pending}
                  >
                    <i className={`bi-archive${item.archivado ? '-fill' : ''}`} />
                    {item.archivado ? 'Desarchivar' : 'Archivar'}
                  </button>
                )}
              </div>
            </div>

            {/* Item Card */}
            <div className="AIDP-item-card">
              {/* Image Section */}
              <div className="AIDP-item-image-wrapper">
                {mainImage ? (
                  <img
                    src={resolveImageUrl(mainImage.image_url)}
                    alt={item.NombreItem}
                    className="AIDP-item-image"
                  />
                ) : (
                  <div className="AIDP-image-placeholder">
                    <i className="bi-box-seam" />
                    <p>Sin imagen</p>
                  </div>
                )}
                {hasEditItems && (
                  <div className="AIDP-image-overlay">
                    <button
                      type="button"
                      className="AIDP-image-button"
                      onClick={() => setShowImageModal(true)}
                    >
                      <i className="bi-camera-video" />
                      {images.length > 0 ? 'Cambiar imagen' : 'Agregar imagen'}
                    </button>
                  </div>
                )}
              </div>

              {/* Content Section */}
              <div className="AIDP-item-content">
                {/* Name */}
                <div className="AIDP-field-item">
                  <div className="AIDP-field-header">
                    <label className="AIDP-field-label">Nombre del Item</label>
                    {hasEditItems && !editState.name.editing && (
                      <button
                        type="button"
                        className="AIDP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, name: { ...prev.name, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.name.editing ? (
                    <div className="AIDP-field-edit">
                      <input
                        type="text"
                        className="AIDP-field-input"
                        value={editState.name.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          name: { ...prev.name, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('name', editState.name.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveField('name', editState.name.value);
                          } else if (e.key === 'Escape') {
                            setEditState(prev => ({ ...prev, name: { ...prev.name, editing: false } }));
                          }
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="AIDP-field-value">
                      {item.NombreItem || 'Sin nombre'}
                    </div>
                  )}
                </div>

                {/* SKU */}
                <div className="AIDP-field-item">
                  <div className="AIDP-field-header">
                    <label className="AIDP-field-label">SKU</label>
                    {hasEditItems && !editState.sku.editing && (
                      <button
                        type="button"
                        className="AIDP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, sku: { ...prev.sku, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.sku.editing ? (
                    <div className="AIDP-field-edit">
                      <input
                        type="text"
                        className="AIDP-field-input AIDP-field-input-mono"
                        value={editState.sku.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          sku: { ...prev.sku, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('sku', editState.sku.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveField('sku', editState.sku.value);
                          } else if (e.key === 'Escape') {
                            setEditState(prev => ({ ...prev, sku: { ...prev.sku, editing: false } }));
                          }
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="AIDP-field-value AIDP-field-value-mono">
                      {item.sku || 'N/A'}
                    </div>
                  )}
                </div>

                {/* Short Description */}
                <div className="AIDP-field-item AIDP-field-full-width">
                  <div className="AIDP-field-header">
                    <label className="AIDP-field-label">Descripción Breve</label>
                    {hasEditItems && !editState.shortDescription.editing && (
                      <button
                        type="button"
                        className="AIDP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, shortDescription: { ...prev.shortDescription, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.shortDescription.editing ? (
                    <div className="AIDP-field-edit">
                      <textarea
                        className="AIDP-field-textarea"
                        rows={3}
                        value={editState.shortDescription.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          shortDescription: { ...prev.shortDescription, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('shortDescription', editState.shortDescription.value)}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="AIDP-field-value">
                      {item.descripcionbreveitem || 'Sin descripción breve'}
                    </div>
                  )}
                </div>

                {/* Full Description */}
                <div className="AIDP-field-item AIDP-field-full-width">
                  <div className="AIDP-field-header">
                    <label className="AIDP-field-label">Descripción Completa</label>
                    {hasEditItems && !editState.fullDescription.editing && (
                      <button
                        type="button"
                        className="AIDP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, fullDescription: { ...prev.fullDescription, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.fullDescription.editing ? (
                    <div className="AIDP-field-edit">
                      <textarea
                        className="AIDP-field-textarea"
                        rows={5}
                        value={editState.fullDescription.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          fullDescription: { ...prev.fullDescription, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('fullDescription', editState.fullDescription.value)}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="AIDP-field-value">
                      {item.descripcioncompletaitem || 'Sin descripción completa'}
                    </div>
                  )}
                </div>

                {/* Stock and Offer Row */}
                <div className="AIDP-field-row">
                  <div className="AIDP-field-item">
                    <div className="AIDP-field-header">
                      <label className="AIDP-field-label">Stock Disponible</label>
                      {hasEditItems && !editState.stock.editing && (
                        <button
                          type="button"
                          className="AIDP-field-edit-btn"
                          onClick={() => setEditState(prev => ({ ...prev, stock: { ...prev.stock, editing: true } }))}
                        >
                          <i className="bi-pencil" />
                        </button>
                      )}
                    </div>
                    {editState.stock.editing ? (
                      <div className="AIDP-field-edit">
                        <input
                          type="number"
                          min="0"
                          className="AIDP-field-input"
                          value={editState.stock.value}
                          onChange={(e) => setEditState(prev => ({
                            ...prev,
                            stock: { ...prev.stock, value: parseInt(e.target.value) || 0 },
                          }))}
                          onBlur={() => handleSaveField('stock', editState.stock.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveField('stock', editState.stock.value);
                            } else if (e.key === 'Escape') {
                              setEditState(prev => ({ ...prev, stock: { ...prev.stock, editing: false } }));
                            }
                          }}
                          autoFocus
                        />
                      </div>
                    ) : (
                      <div className="AIDP-field-value">
                        {item.stockdisponible ?? 0}
                      </div>
                    )}
                  </div>

                  <div className="AIDP-field-item">
                    <div className="AIDP-field-header">
                      <label className="AIDP-field-label">Oferta (%)</label>
                      {hasEditItems && !editState.offer.editing && (
                        <button
                          type="button"
                          className="AIDP-field-edit-btn"
                          onClick={() => setEditState(prev => ({ ...prev, offer: { ...prev.offer, editing: true } }))}
                        >
                          <i className="bi-pencil" />
                        </button>
                      )}
                    </div>
                    {editState.offer.editing ? (
                      <div className="AIDP-field-edit">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          className="AIDP-field-input"
                          value={editState.offer.value || ''}
                          onChange={(e) => setEditState(prev => ({
                            ...prev,
                            offer: { ...prev.offer, value: e.target.value ? parseFloat(e.target.value) : null },
                          }))}
                          onBlur={() => handleSaveField('offer', editState.offer.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveField('offer', editState.offer.value);
                            } else if (e.key === 'Escape') {
                              setEditState(prev => ({ ...prev, offer: { ...prev.offer, editing: false } }));
                            }
                          }}
                          autoFocus
                        />
                      </div>
                    ) : (
                      <div className="AIDP-field-value">
                        {item.Oferta ? `${item.Oferta}%` : 'Sin oferta'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Category */}
                <div className="AIDP-field-item">
                  <div className="AIDP-field-header">
                    <label className="AIDP-field-label">Categoría</label>
                    {hasEditItems && !editState.category.editing && (
                      <button
                        type="button"
                        className="AIDP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, category: { ...prev.category, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.category.editing ? (
                    <div className="AIDP-field-edit">
                      <select
                        className="AIDP-field-select"
                        value={editState.category.value || ''}
                        onChange={(e) => {
                          const newValue = e.target.value ? parseInt(e.target.value) : null;
                          setEditState(prev => ({
                            ...prev,
                            category: { ...prev.category, value: newValue },
                          }));
                          handleSaveField('category', newValue);
                        }}
                        onBlur={() => setEditState(prev => ({ ...prev, category: { ...prev.category, editing: false } }))}
                        autoFocus
                      >
                        <option value="">Selecciona una categoría</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.NombreCategoria}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="AIDP-field-value">
                      {categoryName}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Images Section */}
            <section className="AIDP-images-section">
              <div className="AIDP-section-header" onClick={() => setExpandedImages(!expandedImages)}>
                <h2 className="AIDP-section-title">
                  <i className={`bi-chevron-${expandedImages ? 'down' : 'right'} AIDP-section-chevron`} />
                  Imágenes del Item
                  {images.length > 0 && (
                    <span className="AIDP-section-count">({images.length})</span>
                  )}
                </h2>
                {expandedImages && hasEditItems && (
                  <button
                    type="button"
                    className="AIDP-section-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowImageModal(true);
                    }}
                  >
                    <i className="bi-plus-circle" />
                    Agregar Imagen
                  </button>
                )}
              </div>
              {expandedImages && (
                <div className="AIDP-images-carousel-wrapper">
                  {images.length === 0 ? (
                    <p className="AIDP-empty-text">No hay imágenes agregadas.</p>
                  ) : (
                    <>
                      {images.length > 2 && (
                        <button
                          type="button"
                          className="AIDP-carousel-btn AIDP-carousel-btn-prev"
                          onClick={() => {
                            const maxIndex = images.length % 2 === 0 ? images.length - 2 : images.length - 1;
                            setCurrentImageIndex((prev) => (prev === 0 ? maxIndex : Math.max(0, prev - 2)));
                          }}
                          aria-label="Imagen anterior"
                        >
                          <i className="bi-chevron-left" />
                        </button>
                      )}
                      <div className="AIDP-images-carousel">
                        <div
                          className="AIDP-images-carousel-track"
                          style={{
                            transform: `translateX(-${currentImageIndex * 50}%)`,
                          }}
                        >
                          {images.map((img, index) => (
                            <div key={img.id || index} className="AIDP-image-card">
                              <img
                                src={resolveImageUrl(img.image_url)}
                                alt={img.alt_text || `Imagen ${index + 1}`}
                                className="AIDP-image-thumb"
                              />
                              <div className="AIDP-image-actions">
                                <span className="AIDP-badge AIDP-badge-secondary">Posición {img.position + 1}</span>
                                {hasEditItems && (
                                  <button
                                    type="button"
                                    className="AIDP-image-delete-btn"
                                    onClick={() => handleDeleteImage(img.id, img.position)}
                                  >
                                    <i className="bi-trash" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {images.length > 2 && (
                        <button
                          type="button"
                          className="AIDP-carousel-btn AIDP-carousel-btn-next"
                          onClick={() => {
                            const maxIndex = images.length % 2 === 0 ? images.length - 2 : images.length - 1;
                            setCurrentImageIndex((prev) => (prev >= maxIndex ? 0 : prev + 2));
                          }}
                          aria-label="Imagen siguiente"
                        >
                          <i className="bi-chevron-right" />
                        </button>
                      )}
                      {images.length > 2 && (
                        <div className="AIDP-carousel-indicators">
                          {Array.from({ length: Math.ceil(images.length / 2) }).map((_, idx) => {
                            const slideIndex = idx * 2;
                            const maxIndex = images.length % 2 === 0 ? images.length - 2 : images.length - 1;
                            const isActive = currentImageIndex === slideIndex || 
                              (currentImageIndex > slideIndex && currentImageIndex <= Math.min(slideIndex + 1, maxIndex));
                            return (
                              <button
                                key={idx}
                                type="button"
                                className={`AIDP-carousel-indicator ${isActive ? 'active' : ''}`}
                                onClick={() => setCurrentImageIndex(Math.min(slideIndex, maxIndex))}
                                aria-label={`Ir a página ${idx + 1}`}
                              />
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </section>

            {/* Variantes (color, etc.) */}
            <section className="AIDP-attributes-section">
              <div className="AIDP-section-header" onClick={() => setExpandedVariantes(!expandedVariantes)}>
                <h2 className="AIDP-section-title">
                  <i className={`bi-chevron-${expandedVariantes ? 'down' : 'right'} AIDP-section-chevron`} />
                  Variantes del producto
                </h2>
                {expandedVariantes && hasEditItems && (
                  <div className="AIDP-section-actions">
                    <button
                      type="button"
                      className="AIDP-section-btn AIDP-section-btn-outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddVariantRow();
                      }}
                    >
                      <i className="bi-plus-circle" />
                      Agregar variante
                    </button>
                    <button
                      type="button"
                      className="AIDP-section-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveVariantes();
                      }}
                      disabled={variantLoading}
                    >
                      {variantLoading ? (
                        <>
                          <Spinner size="sm" className="AIDP-spinner" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <i className="bi-save" />
                          Guardar variantes
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              {expandedVariantes && (
                <div className="AIDP-attributes-content">
                  <p className="AIDP-empty-text" style={{ marginBottom: '1rem' }}>
                    Cada variante tiene precio, stock, oferta (%) e imagen propia. Subí el archivo y guardá variantes.
                  </p>
                  {variantes.length === 0 ? (
                    <p className="AIDP-empty-text">No hay variantes. Agregá filas para color, talla, etc.</p>
                  ) : (
                    <Table bordered size="sm" className="AIDP-attributes-table">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Precio (ARS)</th>
                          <th>OfertaDeVariable (%)</th>
                          <th>Imagen</th>
                          <th>Stock</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {variantes.map((row, index) => (
                          <tr key={row.id || `new-${index}`}>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.nombre_variante}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setVariantes((prev) =>
                                    prev.map((r, i) => (i === index ? { ...r, nombre_variante: val } : r)),
                                  );
                                }}
                                placeholder="Ej: Azul"
                              />
                            </td>
                            <td style={{ maxWidth: '120px' }}>
                              <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.precio_variante}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setVariantes((prev) =>
                                    prev.map((r, i) =>
                                      i === index ? { ...r, precio_variante: val === '' ? '' : Number(val) } : r,
                                    ),
                                  );
                                }}
                              />
                            </td>
                            <td style={{ maxWidth: '110px' }}>
                              <Form.Control
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={row.oferta_variante ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setVariantes((prev) =>
                                    prev.map((r, i) =>
                                      i === index
                                        ? {
                                            ...r,
                                            oferta_variante: val === '' ? '' : Number(val),
                                          }
                                        : r,
                                    ),
                                  );
                                }}
                                placeholder="0"
                              />
                            </td>
                            <td className="AIDP-variant-image-cell">
                              <div className="AIDP-variant-image-box">
                                {row.imagen_variante ? (
                                  <img
                                    src={resolveImageUrl(row.imagen_variante)}
                                    alt={row.nombre_variante || 'Variante'}
                                    className="AIDP-variant-image-preview"
                                  />
                                ) : (
                                  <div className="AIDP-variant-image-placeholder">
                                    <i className="bi-image" />
                                  </div>
                                )}
                                <div className="AIDP-variant-image-actions">
                                  <label
                                    className={`AIDP-variant-image-upload-btn ${
                                      variantImageUploadIndex === index ? 'is-loading' : ''
                                    }`}
                                  >
                                    <input
                                      type="file"
                                      accept="image/*"
                                      hidden
                                      disabled={variantImageUploadIndex != null}
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleVariantImageUpload(index, file);
                                        e.target.value = '';
                                      }}
                                    />
                                    {variantImageUploadIndex === index ? (
                                      <>
                                        <Spinner animation="border" size="sm" />
                                        <span>Subiendo…</span>
                                      </>
                                    ) : (
                                      <>
                                        <i className="bi-upload" />
                                        <span>{row.imagen_variante ? 'Cambiar' : 'Subir'}</span>
                                      </>
                                    )}
                                  </label>
                                  {row.imagen_variante ? (
                                    <button
                                      type="button"
                                      className="AIDP-variant-image-clear-btn"
                                      title="Quitar imagen"
                                      disabled={variantImageUploadIndex != null}
                                      onClick={() => handleClearVariantImage(index)}
                                    >
                                      <i className="bi-x-lg" />
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                            <td style={{ maxWidth: '100px' }}>
                              <Form.Control
                                type="number"
                                min="0"
                                value={row.stock_variante}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setVariantes((prev) =>
                                    prev.map((r, i) =>
                                      i === index
                                        ? { ...r, stock_variante: val === '' ? '' : parseInt(val, 10) || 0 }
                                        : r,
                                    ),
                                  );
                                }}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="AIDP-table-btn AIDP-table-btn-delete"
                                onClick={() => handleRemoveVariantRow(index)}
                              >
                                <i className="bi-trash" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              )}
            </section>

            {/* Attributes Section */}
            <section className="AIDP-attributes-section">
              <div className="AIDP-section-header" onClick={() => setExpandedAttributes(!expandedAttributes)}>
                <h2 className="AIDP-section-title">
                  <i className={`bi-chevron-${expandedAttributes ? 'down' : 'right'} AIDP-section-chevron`} />
                  Atributos del Item
                </h2>
                {expandedAttributes && hasEditItems && (
                  <div className="AIDP-section-actions">
                    <button
                      type="button"
                      className="AIDP-section-btn AIDP-section-btn-outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAttributeDraft({ id: null, key: '', value: '' });
                        setShowAttributeModal(true);
                      }}
                    >
                      <i className="bi-plus-circle" />
                      Agregar Atributo
                    </button>
                    <button
                      type="button"
                      className="AIDP-section-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveAttributes();
                      }}
                      disabled={attributeLoading}
                    >
                      {attributeLoading ? (
                        <>
                          <Spinner size="sm" className="AIDP-spinner" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <i className="bi-save" />
                          Guardar Cambios
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              {expandedAttributes && (
                <div className="AIDP-attributes-content">
                  {attributes.length === 0 ? (
                    <p className="AIDP-empty-text">No hay atributos agregados.</p>
                  ) : (
                    <table className="AIDP-attributes-table">
                      <thead>
                        <tr>
                          <th>Clave</th>
                          <th>Valor</th>
                          {hasEditItems && <th>Acciones</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {attributes.map((attr, index) => (
                          <tr key={attr.id || index}>
                            <td>{attr.attribute_key}</td>
                            <td>{attr.attribute_value}</td>
                            {hasEditItems && (
                              <td>
                                <button
                                  type="button"
                                  className="AIDP-table-btn AIDP-table-btn-edit"
                                  onClick={() => handleEditAttribute(index)}
                                >
                                  <i className="bi-pencil" />
                                </button>
                                <button
                                  type="button"
                                  className="AIDP-table-btn AIDP-table-btn-delete"
                                  onClick={() => handleDeleteAttribute(index)}
                                >
                                  <i className="bi-trash" />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </section>
          </div>
        </main>
      </div>

      {/* Image Upload Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Subir Imagen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Seleccionar Imagen</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleUploadImage}
            disabled={!imageFile || imageLoading}
          >
            {imageLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Subiendo...
              </>
            ) : (
              <>
                <i className="bi-upload me-2" />
                Subir Imagen
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Attribute Modal */}
      <Modal show={showAttributeModal} onHide={() => setShowAttributeModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {attributeDraft.id ? 'Editar Atributo' : 'Agregar Atributo'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Clave *</Form.Label>
            <Form.Control
              type="text"
              value={attributeDraft.key}
              onChange={(e) => setAttributeDraft(prev => ({ ...prev, key: e.target.value }))}
              placeholder="Ej: Color, Talla, Material"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Valor *</Form.Label>
            <Form.Control
              type="text"
              value={attributeDraft.value}
              onChange={(e) => setAttributeDraft(prev => ({ ...prev, value: e.target.value }))}
              placeholder="Ej: Rojo, M, Algodón"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAttributeModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={attributeDraft.id ? handleUpdateAttribute : handleAddAttribute}
            disabled={!attributeDraft.key.trim() || !attributeDraft.value.trim()}
          >
            {attributeDraft.id ? 'Actualizar' : 'Agregar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminItemDetallePage = () => (
  <AdminNotificationsProvider>
    <AdminItemDetalleInner />
  </AdminNotificationsProvider>
);

export default AdminItemDetallePage;
